import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AppShell } from '../components/Layout';
import { BlockView, useDocumentMeta } from '../components/Prose';
import { formatDate } from '../lib/format';
import { useSession } from '../state/session';
import { CATEGORIES, POSTS, type Post, postBySlug, related }
  from '../content/posts';

function marketingNav(user: unknown) {
  return {
    nav: [
      { to: '/pricing', label: 'Pricing' },
      { to: '/blog', label: 'Blog' },
      { to: '/verify', label: 'Verify a document' },
    ],
    actions: user
      ? <Link className="btn btn--primary btn--small" to="/app">Dashboard</Link>
      : (
        <>
          <Link className="btn btn--small btn--ghost" to="/login">Sign in</Link>
          <Link className="btn btn--primary btn--small" to="/register">Sign up</Link>
        </>
      ),
  };
}

function PostCard({ post }: { post: Post }) {
  return (
    <Link className="card postcard" to={`/blog/${post.slug}`}>
      <span className="postcard__meta">
        <span className="pill pill--brand">{post.category}</span>
        <span className="small muted">{post.minutes} min read</span>
      </span>
      <span className="postcard__title">{post.title}</span>
      <span className="postcard__lede">{post.description}</span>
      <span className="spacer" />
      <span className="small muted">{formatDate(post.published)}</span>
    </Link>
  );
}

export function Blog() {
  const { user } = useSession();
  const [category, setCategory] = useState<string>('All');
  useDocumentMeta(
    'Blog — eSignLoop',
    'Comparisons, guides and evidence explainers for choosing and using an '
    + 'electronic signature product.');

  const shown = useMemo(
    () => (category === 'All' ? POSTS : POSTS.filter((p) => p.category === category)),
    [category]);

  const { nav, actions } = marketingNav(user);
  return (
    <AppShell nav={nav} actions={actions}>
      <section className="shell section">
        <header style={{ maxWidth: '62ch' }}>
          <h1 style={{ margin: 0 }}>Writing</h1>
          <p className="hero__lede" style={{ marginTop: 12 }}>
            How to choose an e-signature product, what an audit trail is
            actually worth, and where this one is weaker than the alternatives.
            No invented competitor pricing and no claims we cannot show.
          </p>
        </header>

        <div className="row row--tight" style={{ marginTop: 24 }}
             role="tablist" aria-label="Filter articles">
          {['All', ...CATEGORIES].map((item) => (
            <button key={item} type="button" role="tab"
                    aria-selected={category === item}
                    className={`btn btn--small${category === item ? ' btn--primary' : ' btn--ghost'}`}
                    onClick={() => setCategory(item)}>
              {item}
            </button>
          ))}
        </div>

        <div className="grid grid--cards" style={{ marginTop: 20 }}>
          {shown.map((post) => <PostCard post={post} key={post.slug} />)}
        </div>
      </section>
    </AppShell>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const { user } = useSession();
  const post = postBySlug(slug);

  useDocumentMeta(
    post ? `${post.title} — eSignLoop` : 'Article not found — eSignLoop',
    post?.description ?? '');

  const { nav, actions } = marketingNav(user);

  if (!post) {
    return (
      <AppShell nav={nav} actions={actions}>
        <section className="shell section center">
          <h1>We could not find that article</h1>
          <p className="muted">It may have been renamed.</p>
          <Link className="btn btn--primary" to="/blog">Back to all writing</Link>
        </section>
      </AppShell>
    );
  }

  return (
    <AppShell nav={nav} actions={actions}>
      <article className="shell shell--narrow section">
        <Link className="small muted" to="/blog">← All writing</Link>
        <header style={{ marginTop: 16 }}>
          <div className="row row--tight">
            <span className="pill pill--brand">{post.category}</span>
            <span className="small muted">
              {formatDate(post.published)} · {post.minutes} min read
            </span>
          </div>
          <h1 style={{ margin: '14px 0 0' }}>{post.title}</h1>
          <p className="hero__lede" style={{ marginTop: 12 }}>{post.description}</p>
        </header>

        <div className="prose">
          {post.body.map((block, index) => <BlockView block={block} key={index} />)}
        </div>

        <div className="cta" style={{ marginTop: 40 }}>
          <h2>See it rather than read about it</h2>
          <p>
            Preparing an agreement costs nothing and needs no account. A card is
            required only when you send.
          </p>
          <div className="btn-row btn-row--stack" style={{ justifyContent: 'center' }}>
            <Link className="btn btn--primary btn--large" to="/register">
              Create your account
            </Link>
            <Link className="btn btn--large" to="/pricing">Compare pricing</Link>
          </div>
        </div>

        <section style={{ marginTop: 44 }} aria-labelledby="related">
          <h2 id="related" style={{ fontSize: '1.15rem' }}>Read next</h2>
          <div className="grid grid--cards" style={{ marginTop: 16 }}>
            {related(post).map((item) => <PostCard post={item} key={item.slug} />)}
          </div>
        </section>
      </article>
    </AppShell>
  );
}
